import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  User, 
  Bot, 
  Clock,
  StopCircle 
} from 'lucide-react';
import { useInterviewSimulation, InterviewType, InterviewMode, InterviewDifficulty } from '@/hooks/useInterviewSimulation';
import { InterviewFeedback } from './InterviewFeedback';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface InterviewChatProps {
  type: InterviewType;
  mode: InterviewMode;
  category: string;
  difficulty: InterviewDifficulty;
  onBack: () => void;
  onEnd: () => void;
}

const typeLabels: Record<InterviewType, string> = {
  technical: 'Técnica',
  behavioral: 'Comportamental',
  business_case: 'Case de Negócio',
};

const difficultyLabels: Record<InterviewDifficulty, string> = {
  junior: 'Júnior',
  pleno: 'Pleno',
  senior: 'Sênior',
};

export function InterviewChat({ 
  type, 
  mode, 
  category, 
  difficulty, 
  onBack, 
  onEnd 
}: InterviewChatProps) {
  const {
    messages,
    isLoading,
    feedback,
    status,
    isReady,
    isInitializing,
    isAuthLoading,
    isAuthReady,
    startSimulation,
    sendMessage,
    endSimulation,
    abandonSimulation,
    resetSimulation,
  } = useInterviewSimulation();

  const [input, setInput] = useState('');
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const startAttemptedRef = useRef(false);

  // Start simulation reactively - wait for auth to be ready
  useEffect(() => {
    if (startAttemptedRef.current) return;
    if (isAuthLoading || !isAuthReady) return;

    startAttemptedRef.current = true;
    startSimulation({ type, mode, category, difficulty });
  }, [isAuthLoading, isAuthReady, type, mode, category, difficulty, startSimulation]);

  // Timer
  useEffect(() => {
    if (status === 'in_progress') {
      const interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [status]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !isReady) return;
    const msg = input;
    setInput('');
    const success = await sendMessage(msg);
    if (!success) {
      setInput(msg); // restore on failure
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleEnd = async () => {
    setShowEndConfirm(false);
    await endSimulation();
  };

  const handleAbandon = () => {
    setShowAbandonConfirm(false);
    abandonSimulation();
    onBack();
  };

  const handleNewSimulation = () => {
    resetSimulation();
    onEnd();
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Show feedback when simulation is completed
  if (status === 'completed' && feedback) {
    return (
      <InterviewFeedback 
        feedback={feedback} 
        duration={elapsedTime}
        onNewSimulation={handleNewSimulation}
      />
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] min-h-[500px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setShowAbandonConfirm(true)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h2 className="font-semibold">Entrevista {typeLabels[type]}</h2>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline" className="text-xs">{difficultyLabels[difficulty]}</Badge>
              {category !== 'default' && (
                <Badge variant="outline" className="text-xs">{category.toUpperCase()}</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="font-mono">
            <Clock className="h-3 w-3 mr-1" />
            {formatTime(elapsedTime)}
          </Badge>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => setShowEndConfirm(true)}
            disabled={isLoading}
          >
            <StopCircle className="h-4 w-4 mr-1" />
            Finalizar
          </Button>

        </div>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div 
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  message.role === 'user' 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted'
                }`}>
                  {message.role === 'user' ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </div>
                <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                </div>
              </div>
            ))}

            {(isLoading || isInitializing) && messages.length === 0 && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="bg-muted rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">
                      {isInitializing ? 'Iniciando simulação...' : 'Gerando pergunta...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isReady ? "Digite sua resposta..." : "Aguardando início da simulação..."}
              className="resize-none min-h-[60px]"
              disabled={isLoading || !isReady}
            />
            <Button type="submit" disabled={!input.trim() || isLoading || !isReady} className="shrink-0">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-2">
            Pressione Enter para enviar, Shift+Enter para nova linha
          </p>
        </div>
      </Card>

      {/* End Confirmation Dialog */}
      <AlertDialog open={showEndConfirm} onOpenChange={setShowEndConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar entrevista?</AlertDialogTitle>
            <AlertDialogDescription>
              Você pode encerrar quando quiser. A IA vai gerar um feedback com o que já foi conversado — sem pressão.
            </AlertDialogDescription>

          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar entrevista</AlertDialogCancel>
            <AlertDialogAction onClick={handleEnd}>
              Finalizar e ver feedback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Abandon Confirmation Dialog */}
      <AlertDialog open={showAbandonConfirm} onOpenChange={setShowAbandonConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abandonar entrevista?</AlertDialogTitle>
            <AlertDialogDescription>
              Você perderá todo o progresso desta simulação. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar</AlertDialogCancel>
            <AlertDialogAction onClick={handleAbandon} className="bg-destructive text-destructive-foreground">
              Abandonar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
